<template>
    <div class="container">
        <div class="row">
            <heading>
                <template v-if="data.is_return">
                    {{ $t('booking.creadit_return_heading') }}
                </template>
                <template v-else>
                    {{ $t('booking.menuName') }}
                </template>
            </heading>
        </div>
        <div class="row">
<!--     SENDER       -->
            <Card class="col-12 col-md-6" :heading="$t('booking.sender')">
                <LGTInput
                    :label="$t('booking.fields.referenceNumber')"
                    v-model="data.sender.reference_number"
                    error-key="sender.reference_number"
                    :tooltip="$t('booking.tooltips.senderReferenceNumber')"
                    :errors="errors"
                    required
                >
                    <template v-slot:afterInput>
                        <div class="text-right small"><character-counter :limit="50" :value="data.sender.reference_number"></character-counter></div>
                    </template>
                </LGTInput>

                <div class="form-group" v-if="data.is_return">
                    <label>{{ $t('booking.sender') }}</label>
                    <multiselect
                        v-model="selectedRecipient"
                        track-by="id"
                        label="name"
                        :options="recipients"
                        @input="recipientSelected"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                        <template slot="option" slot-scope="props">
                            <div class="d-flex justify-content-between">
                                <span>
                                    {{ props.option.name }}
                                </span>
                                <span class="text-danger font-weight-bold" @click.prevent.stop="deleteAddress('recipients', props.option)">X</span>
                            </div>
                        </template>
                    </multiselect>
                </div>
                <div class="form-group" v-if="!data.is_return">
                    <label>{{ $t('booking.sender') }}</label>
                    <multiselect
                        v-model="selectedSenderAddress"
                        track-by="id"
                        label="name"
                        :options="senderAddresses"
                        @input="senderSelected"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                        <template slot="option" slot-scope="props">
                            <div class="d-flex justify-content-between">
                                <span>
                                    {{ props.option.name }}
                                </span>
                                <span class="text-danger font-weight-bold" @click.prevent.stop="deleteAddress('senderAddresses', props.option)">X</span>
                            </div>
                        </template>
                    </multiselect>
                </div>


                <LGTInput
                    :label="$t('booking.fields.name')"
                    v-model="data.sender.name"
                    error-key="sender.name"
                    :errors="errors"
                    required
                ></LGTInput>
                <div class="form-group">
                    <address-search-input
                        v-model="data.sender.address"
                        :label="$t('booking.fields.address')"
                        @details="simpleAddressChanged('sender', $event)"
                        :required="true"
                    ></address-search-input>
                    <small class="text-danger" v-if="errors['sender.address']">{{ errors['sender.address'][0] }}</small>
                </div>
                <div class="form-group">
                    <label class="">{{ $t('booking.fields.country') }}
                        <required-icon></required-icon>
                    </label>
                    <multiselect
                        v-model="data.sender.country"
                        track-by="code"
                        label="name"
                        :options="countries"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                    </multiselect>
                    <small class="text-danger" v-if="errors['sender.country']">{{ errors['sender.country'][0] }}</small>
                </div>
                <LGTInput
                    :label="$t('booking.fields.postal_code')"
                    v-model="data.sender.postal_code"
                    error-key="sender.postal_code"
                    :errors="errors"
                    required
                ></LGTInput>
                <LGTInput
                    :label="$t('booking.fields.city')"
                    v-model="data.sender.city"
                    error-key="sender.city"
                    :errors="errors"
                    required
                ></LGTInput>
                <div class="form-group">
                    <label class="">{{ $t('booking.fields.phone') }}
                    </label>
                    <div class="input-group">
                        <select class="form-control" style="max-width: 90px;" v-model="data.sender.phone_prefix">
                            <option :value="country.phone_prefix" v-for="country in countries">{{ country.phone_prefix }}</option>
                        </select>
                        <input type="number" v-model="data.sender.phone" class="form-control">
                    </div>
                    <small class="text-danger" v-if="errors['sender.phone']">{{ errors['sender.phone'][0] }}</small>
                </div>

                <template v-if="data.is_return">
                    <div class="form-group">
                        <label>
                            {{ $t('booking.fields.imageDocumentation') }}
                            <required-icon v-if="imageDocumentationRequiredOnReturn"></required-icon>
                        </label>
                        <b-form-file v-model="imageDocumentationFieldData" accept="image/*" ref="image-documentation-input" :placeholder="$t('core.filePlaceholder')" multiple :browse-text="$t('core.chooseFiles')" @input="imageDocumentationAdded"></b-form-file>
                        <small class="text-danger" v-if="errors['imageDocumentation']">{{ errors['imageDocumentation'][0] }}</small>
                        <div v-for="(file, fileIndex) in data.imageDocumentation">
                            {{ file.name }} <small @click="data.imageDocumentation.splice(fileIndex, 1); delete imageDocumentationErrors[fileIndex]" class="user-select-none text-danger">{{ $t('core.actions.remove') }}</small>
                            <small class="text-danger" v-if="imageDocumentationErrors[fileIndex]"><br/>{{ imageDocumentationErrors[fileIndex] }}</small>
                        </div>
                        <div v-for="(file, fileIndex) in data.imageDocumentations">
                            {{ file.filename }} <small @click="deleteExistingImageDocumentation(fileIndex)" class="user-select-none text-danger">{{ $t('core.actions.remove') }}</small>
                        </div>
                    </div>
                </template>

                <div class="custom-control custom-checkbox" v-show="!data.is_return">
                    <input type="checkbox" class="custom-control-input" id="sender-send-label" v-model="data.sender.send_label">
                    <label class="custom-control-label" for="sender-send-label">{{ $t('booking.fields.send_label') }}</label>
                </div>
                <div class="custom-control custom-checkbox">
                    <input type="checkbox" class="custom-control-input" id="future-sender" v-model="data.sender.forFutureUse">
                    <label class="custom-control-label" for="future-sender">{{ $t('booking.fields.saveAsFutureSender') }}</label>
                    <small class="text-danger" v-if="errors['sender.forFutureUse']">{{ errors['sender.forFutureUse'][0] }}</small>
                </div>

                <LGTInput
                    v-if="data.sender.send_label && !data.is_return"
                    :label="$t('booking.fields.label_email')"
                    v-model="data.sender.label_email"
                    error-key="sender.label_email"
                    :errors="errors"
                    required
                ></LGTInput>

                <div class="form-group" v-if="data.sender.send_label && data.is_return">
                    <label>{{ $t('booking.fields.label_email') }}</label>
                    <multiselect
                        v-model="data.sender.label_email"
                        :options="senderLabelEmailOptions"
                        :taggable="true"
                        track-by="email"
                        label="email"
                        :multiple="true"
                        @tag="addSenderEmail"
                        @close="senderLabelEmailClose"
                        @search-change="senderLabelEmailSearchQueryChange"
                        :tag-placeholder="$t('booking.fields.label_email')"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                    </multiselect>
                    <small class="text-danger" v-if="errors['sender.label_email']">{{ errors['sender.label_email'][0] }}</small>
                    <small class="text-danger" v-if="Object.keys(errors).filter((x) => x.startsWith('sender.label_email.')).length > 0">{{ $t('validation.custom.all_emails_must_be_valid') }}</small>
                </div>


                <div class="form-group">
                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="load-address-check" v-model="loadAddressShow">
                        <label class="custom-control-label" for="load-address-check">{{ $t('booking.fields.loadAddressSelected') }}</label>
                    </div>
                </div>

                <template v-if="loadAddressShow">
                    <div class="form-group" v-if="!data.is_return">
                        <label>{{ $t('booking.loadAddress') }}</label>
                        <multiselect
                            v-model="selectedLoadAddress"
                            track-by="id"
                            label="name"
                            :options="loadAddresses"
                            @input="loadAddressSelected"
                            :placeholder="$t('third_party.multiselect.placeholder')"
                            selectLabel=""
                            selectedLabel=""
                            deselectLabel=""
                        >
                            <template v-slot:noOptions>
                                {{ $t('third_party.multiselect.noOptions') }}
                            </template>
                            <template slot="option" slot-scope="props">
                                <div class="d-flex justify-content-between">
                                <span>
                                    {{ props.option.name }}
                                </span>
                                    <span class="text-danger font-weight-bold" @click.prevent.stop="deleteAddress('loadAddresses', props.option)">X</span>
                                </div>
                            </template>
                        </multiselect>
                    </div>

                    <div class="form-group position-relative">
                        <label class="">{{ $t('booking.fields.name') }}
                            <required-icon></required-icon>
                        </label>
                        <input type="text" v-model="data.loadAddress.name" class="form-control" @focusin="loadAddressNameFocused = true"
                               @focusout="fieldUnfocus('loadAddressNameFocused')"/>
                        <small class="text-danger" v-if="errors['loadAddress.name']">{{ errors['loadAddress.name'][0] }}</small>
                        <ul class="list-group position-absolute overflow-auto" :class="{'d-none': !loadAddressNameFocused}" style="z-index: 1; max-height: 400px;">
                            <li class="list-group-item" v-for="loadAddress in filteredLoadAddresses" @click="loadAddressSelected(loadAddress)">{{ loadAddress.name }}</li>
                        </ul>

                    </div>
                    <div class="form-group">
                        <address-search-input
                            v-model="data.loadAddress.address"
                            :label="$t('booking.fields.address')"
                            @details="simpleAddressChanged('loadAddress',$event)"
                            :required="true"
                        ></address-search-input>
                        <small class="text-danger" v-if="errors['loadAddress.address']">{{ errors['loadAddress.address'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <label class="">{{ $t('booking.fields.country') }}
                            <required-icon></required-icon>
                        </label>
                        <multiselect
                            v-model="data.loadAddress.country"
                            track-by="code"
                            label="name"
                            :options="countries"
                            :placeholder="$t('third_party.multiselect.placeholder')"
                            selectLabel=""
                            selectedLabel=""
                            deselectLabel=""
                        >
                            <template v-slot:noOptions>
                                {{ $t('third_party.multiselect.noOptions') }}
                            </template>
                        </multiselect>
                        <small class="text-danger" v-if="errors['loadAddress.country']">{{ errors['loadAddress.country'][0] }}</small>
                    </div>
                    <LGTInput
                        :label="$t('booking.fields.postal_code')"
                        v-model="data.loadAddress.postal_code"
                        error-key="loadAddress.postal_code"
                        :errors="errors"
                        required
                    ></LGTInput>
                    <LGTInput
                        :label="$t('booking.fields.city')"
                        v-model="data.loadAddress.city"
                        error-key="loadAddress.city"
                        :errors="errors"
                        required
                    ></LGTInput>
                    <div class="custom-control custom-checkbox" v-if="!data.is_return">
                        <input type="checkbox" class="custom-control-input" id="future-load" v-model="data.loadAddress.forFutureUse">
                        <label class="custom-control-label" for="future-load">{{ $t('booking.fields.saveAsFutureLoad') }}</label>
                        <small class="text-danger" v-if="errors['loadAddress.forFutureUse']">{{ errors['loadAddress.forFutureUse'][0] }}</small>
                    </div>
                </template>
            </Card>
<!--     RECIPIENT       -->
            <Card class="col-12 col-md-6" card-class="mt-5 mt-md-0" :heading="$t('booking.recipient')">
                <LGTInput
                    :label="$t('booking.fields.referenceNumber')"
                    v-model="data.recipient.reference_number"
                    :tooltip="$t('booking.tooltips.recipientReferenceNumber')"
                    error-key="recipient.reference_number"
                    :errors="errors"
                >
                    <template v-slot:afterInput>
                        <div class="text-right small"><character-counter :limit="45" :value="data.recipient.reference_number"></character-counter></div>
                    </template>
                </LGTInput>

                <div class="form-group" v-if="!data.is_return">
                    <label>{{ $t('booking.recipient') }}</label>
                    <multiselect
                        v-model="selectedRecipient"
                        track-by="id"
                        label="name"
                        :options="recipients"
                        @input="recipientSelected"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                        <template slot="option" slot-scope="props">
                            <div class="d-flex justify-content-between">
                                <span>
                                    {{ props.option.name }}
                                </span>
                                <span class="text-danger font-weight-bold" @click.prevent.stop="deleteAddress('recipients', props.option)">X</span>
                            </div>
                        </template>
                    </multiselect>
                </div>
                <div class="form-group" v-if="data.is_return">
                    <label>{{ $t('booking.recipient') }}</label>
                    <multiselect
                        v-model="selectedSenderAddress"
                        track-by="id"
                        label="name"
                        :options="senderAddresses"
                        @input="senderSelected"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                        <template slot="option" slot-scope="props">
                            <div class="d-flex justify-content-between">
                                <span>
                                    {{ props.option.name }}
                                </span>
                                <span class="text-danger font-weight-bold" @click.prevent.stop="deleteAddress('senderAddresses', props.option)">X</span>
                            </div>
                        </template>
                    </multiselect>
                </div>
                <div class="form-group position-relative">
                    <label class="">{{ $t('booking.fields.name') }}
                        <required-icon></required-icon>
                    </label>
                    <input type="text" v-model="data.recipient.name" class="form-control" @focusin="nameFocused = true" @focusout="fieldUnfocus('nameFocused')"/>
                    <small class="text-danger" v-if="errors['recipient.name']">{{ errors['recipient.name'][0] }}</small>
                    <ul class="list-group position-absolute overflow-auto" :class="{'d-none': !nameFocused}" style="z-index: 1; max-height: 400px;">
                        <li class="list-group-item" v-for="recipient in filteredRecipients" @click="recipientSelected(recipient)">{{ recipient.name }}</li>
                    </ul>
                </div>
                <div class="form-group">
                    <address-search-input
                        v-model="data.recipient.address"
                        :label="$t('booking.fields.address')"
                        :required="true"
                        @details="simpleAddressChanged('recipient', $event)"
                    ></address-search-input>
                    <small class="text-danger" v-if="errors['recipient.address']">{{ errors['recipient.address'][0] }}</small>
                </div>
                <div class="form-group">
                    <LGTInput
                        :label="$t('booking.fields.address2')"
                        v-model="data.recipient.address2"
                        error-key="recipient.address2"
                        :errors="errors"
                    ></LGTInput>
                    <small class="text-danger" v-if="errors['recipient.address2']">{{ errors['recipient.address2'][0] }}</small>
                </div>
                <no-delivery-in-country-warning :countries="countries" :country="data.recipient.countryShort"></no-delivery-in-country-warning>
                <div class="form-group">
                    <label class="">{{ $t('booking.fields.country') }}
                        <required-icon></required-icon>
                    </label>
                    <multiselect
                        v-model="data.recipient.country"
                        track-by="code"
                        label="name"
                        :options="countriesAll"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                    </multiselect>
                    <small class="text-danger" v-if="errors['recipient.country']">{{ errors['recipient.country'][0] }}</small>
                </div>
                <div class="form-group">
                    <label class="">{{ $t('booking.fields.postal_code') }}
                        <required-icon></required-icon>
                    </label>
                    <input type="text" class="form-control" v-model="data.recipient.postal_code"/>
                    <small class="text-warning" v-show="citySurcharge">{{ $t('booking.citySurcharge') }}</small>
                    <small class="text-danger" v-if="errors['recipient.postal_code']">{{ errors['recipient.postal_code'][0] }}</small>
                </div>
                <LGTInput
                    :label="$t('booking.fields.city')"
                    v-model="data.recipient.city"
                    error-key="recipient.city"
                    :errors="errors"
                    required
                ></LGTInput>
                <div class="form-group">
                    <label class="">{{ $t('recipient.fields.phone') }}
                        <required-icon v-if="!data.is_return"></required-icon>
                    </label>
                    <div class="input-group">
                        <select class="form-control" style="max-width: 90px;" v-model="data.recipient.phone_prefix">
                            <option :value="country.phone_prefix" v-for="country in countriesAll">{{ country.phone_prefix }}</option>
                        </select>
                        <input type="number" v-model="data.recipient.phone" class="form-control">
                    </div>
                    <small class="text-danger" v-if="errors['recipient.phone']">{{ errors['recipient.phone'][0] }}</small>
                </div>
                <LGTInput
                    :label="$t('recipient.fields.email')"
                    v-model="data.recipient.email"
                    error-key="recipient.email"
                    :errors="errors"
                ></LGTInput>
                <div class="custom-control custom-checkbox">
                    <input type="checkbox" class="custom-control-input" id="future-recipient" v-model="data.recipient.forFutureUse">
                    <label class="custom-control-label" for="future-recipient">{{ $t('booking.fields.saveAsFutureRecipient') }}</label>
                    <small class="text-danger" v-if="errors['recipient.forFutureUse']">{{ errors['recipient.forFutureUse'][0] }}</small>
                </div>

                <div v-if="data.recipient.forFutureUse" class="card p-2">
                    <span class="alert alert-info" v-if="data.is_return === false">{{ $t('booking.labelInfoOnlyForReturn') }}</span>
                    <span class="alert alert-info" v-else>{{ $t('booking.labelInfoOnlyForRegularBooking') }}</span>
                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="recipient-send-label" v-model="data.recipient.send_label">
                        <label class="custom-control-label" for="recipient-send-label">{{ $t('booking.fields.send_label') }}</label>
                    </div>
                    <LGTInput
                        :label="$t('booking.fields.label_email')"
                        v-model="data.recipient.label_email"
                        error-key="recipient.label_email"
                        :errors="errors"
                        :required="data.recipient.send_label"
                    ></LGTInput>
                </div>

                <div class="form-group">
                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="unload-address-check" v-model="unloadAddressShow">
                        <label class="custom-control-label" for="unload-address-check">{{ $t('booking.fields.unloadAddressSelected') }}</label>
                    </div>
                </div>

                <template v-if="unloadAddressShow">
                    <div class="form-group" v-if="!data.is_return">
                        <label>{{ $t('booking.unloadAddress') }}</label>
                        <multiselect
                            v-model="selectedUnloadAddress"
                            track-by="id"
                            label="name"
                            :options="unloadAddresses"
                            @input="unloadAddressSelected"
                            :placeholder="$t('third_party.multiselect.placeholder')"
                            selectLabel=""
                            selectedLabel=""
                            deselectLabel=""
                        >
                            <template v-slot:noOptions>
                                {{ $t('third_party.multiselect.noOptions') }}
                            </template>
                            <template slot="option" slot-scope="props">
                                <div class="d-flex justify-content-between">
                                <span>
                                    {{ props.option.name }}
                                </span>
                                    <span class="text-danger font-weight-bold" @click.prevent.stop="deleteAddress('unloadAddresses', props.option)">X</span>
                                </div>
                            </template>
                        </multiselect>
                    </div>

                    <div class="form-group position-relative">
                        <label class="">{{ $t('booking.fields.name') }}
                            <required-icon></required-icon>
                        </label>
                        <input type="text" v-model="data.unloadAddress.name" class="form-control" @focusin="unloadAddressNameFocused = true"
                               @focusout="fieldUnfocus('unloadAddressNameFocused')"/>
                        <small class="text-danger" v-if="errors['unloadAddress.name']">{{ errors['unloadAddress.name'][0] }}</small>
                        <ul class="list-group position-absolute overflow-auto" :class="{'d-none': !unloadAddressNameFocused}" style="z-index: 1; max-height: 400px;">
                            <li class="list-group-item" v-for="unloadAddress in filteredUnloadAddresses" @click="unloadAddressSelected(unloadAddress)">
                                {{ unloadAddress.name }}
                            </li>
                        </ul>
                    </div>
                    <div class="form-group">
                        <address-search-input
                            v-model="data.unloadAddress.address"
                            :label="$t('booking.fields.address')"
                            @details="simpleAddressChanged('unloadAddress', $event)"
                            :required="true"
                        ></address-search-input>
                        <small class="text-danger" v-if="errors['unloadAddress.address']">{{ errors['unloadAddress.address'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <LGTInput
                            :label="$t('booking.fields.address2')"
                            v-model="data.unloadAddress.address2"
                            error-key="unloadAddress.address2"
                            :errors="errors"
                        ></LGTInput>
                        <small class="text-danger" v-if="errors['unloadAddress.address2']">{{ errors['unloadAddress.address2'][0] }}</small>
                    </div>
                    <no-delivery-in-country-warning :countries="countries" :country="data.unloadAddress.countryShort"></no-delivery-in-country-warning>
                    <div class="form-group">
                        <label class="">{{ $t('booking.fields.country') }}
                            <required-icon></required-icon>
                        </label>
                        <multiselect
                            v-model="data.unloadAddress.country"
                            track-by="code"
                            label="name"
                            :options="countries"
                            :placeholder="$t('third_party.multiselect.placeholder')"
                            selectLabel=""
                            selectedLabel=""
                            deselectLabel=""
                        >
                            <template v-slot:noOptions>
                                {{ $t('third_party.multiselect.noOptions') }}
                            </template>
                        </multiselect>
                        <small class="text-danger" v-if="errors['unloadAddress.country']">{{ errors['unloadAddress.country'][0] }}</small>
                    </div>
                    <LGTInput
                        :label="$t('booking.fields.postal_code')"
                        v-model="data.unloadAddress.postal_code"
                        error-key="unloadAddress.postal_code"
                        :errors="errors"
                        required
                    ></LGTInput>
                    <LGTInput
                        :label="$t('booking.fields.city')"
                        v-model="data.unloadAddress.city"
                        error-key="unloadAddress.city"
                        :errors="errors"
                        required
                    ></LGTInput>
                    <div class="form-group">
                        <label class="">{{ $t('recipient.fields.phone') }}
                            <required-icon v-if="!data.is_return"></required-icon>
                        </label>
                        <div class="input-group">
                            <select class="form-control" style="max-width: 90px;" v-model="data.unloadAddress.phone_prefix">
                                <option :value="country.phone_prefix" v-for="country in countries">{{ country.phone_prefix }}</option>
                            </select>
                            <input type="number" v-model="data.unloadAddress.phone" class="form-control">
                        </div>
                        <small class="text-danger" v-if="errors['unloadAddress.phone']">{{ errors['unloadAddress.phone'][0] }}</small>
                    </div>
                    <LGTInput
                        :label="$t('recipient.fields.email')"
                        v-model="data.unloadAddress.email"
                        error-key="unloadAddress.email"
                        :errors="errors"
                    ></LGTInput>
                    <div class="custom-control custom-checkbox" v-if="!data.is_return">
                        <input type="checkbox" class="custom-control-input" id="future-unload" v-model="data.unloadAddress.forFutureUse">
                        <label class="custom-control-label" for="future-unload">{{ $t('booking.fields.saveAsFutureUnload') }}</label>
                        <small class="text-danger" v-if="errors['unloadAddress.forFutureUse']">{{ errors['unloadAddress.forFutureUse'][0] }}</small>
                    </div>
                </template>
            </Card>
<!--     GOODS       -->
            <Card class="col-12" card-class="mt-5">
                <h2>{{ $t('booking.goods') }}
                    <button class="btn btn-outline-primary float-right" @click="addPackage">{{ $t('core.actions.add') }}</button>
                </h2>
                <small class="text-danger" v-if="errors['lines']">{{ errors['lines'][0] }}</small>
                <table class="table">
                    <thead>
                    <tr>
                        <th class="border-top-0">{{ $t('booking.fields.lineDescription') }}
                            <required-icon></required-icon>
                        </th>
                        <th class="border-top-0">{{ $t('booking.fields.linePackage') }}
                            <required-icon></required-icon>
                        </th>
                        <th class="border-top-0">{{ $t('booking.fields.lineWeight') }}
                            <required-icon></required-icon>
                        </th>
                        <th class="border-top-0">{{ $t('booking.fields.lineVolume') }}
                            <required-icon v-if="volumeRequired"></required-icon>
                        </th>
                        <th class="border-top-0">
                            {{ $t('booking.fields.lineLoadingMeters') }}
                            <required-icon v-if="loadingMetersRequired"></required-icon>
                        </th>
                        <th class="border-top-0" v-if="valueRequired">
                            {{ $t('booking.fields.value') }}
                            <required-icon></required-icon>
                        </th>
                        <th class="border-top-0"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <GoodsCreaditLine
                        v-for="(line, index) in data.lines"
                        :key="'goods-line-' + index"
                        v-model="data.lines[index]"
                        :errors="errors"
                        :index="index"
                        :valueRequired="valueRequired"
                        :currencies="currencies"
                        @remove="data.lines.splice($event, 1)"
                    ></GoodsCreaditLine>
                    </tbody>
                </table>
                <button class="btn btn-outline-primary float-right" @click="addPackage">{{ $t('core.actions.add') }}</button>
            </Card>
<!--     SERVICES       -->
            <Card class="col-12" card-class="mt-5" :heading="$t('booking.services')">
                <div class="form-group">
                    <label>{{ $t('booking.deliveryForm') }}</label>
                    <multiselect
                        v-model="selectedDeliveryForm"
                        track-by="code"
                        label="name"
                        :options="possibleDeliveryForms"
                        @input="deliveryFormSelected"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                    </multiselect>
                    <small class="text-danger" v-if="errors['service_level']">{{ errors['service_level'][0] }}</small>
                </div>
                <div v-if="selectedDeliveryForm !== null" class="mb-4">
                    <div class="custom-control custom-checkbox"
                         v-for="(service, index) in possibleServices">
                        <input type="checkbox" class="custom-control-input" :id="'service' + index" :value="service.code" v-model="data.additional_services">
                        <label class="custom-control-label" :for="'service' + index">{{ service.name }}</label>
                    </div>
                </div>
                <div class="form-group">
                    <label class="d-flex justify-content-between">
                        <span>
                            {{ $t('booking.fields.instructions') }}
                            <img src="/storage/info.png" height="15" v-b-tooltip="$t('booking.tooltips.instructionLength')"/>
                        </span>
                        <character-counter :limit="50" v-model="data.instructions"></character-counter>
                    </label>
                    <textarea type="text" v-model="data.instructions" class="form-control" rows="4"></textarea>
                    <small class="text-danger" v-if="errors['instructions']">{{ errors['instructions'][0] }}</small>

                </div>
                <template v-if="showInvoiceField">
                    <label>
                        {{ (data.is_return) ? $t('booking.fields.invoiceReturn') : $t('booking.fields.invoice') }}
                        <required-icon></required-icon>
                    </label>
                    <b-form-file v-model="invoiceFieldData" ref="invoice-input" :placeholder="$t('core.filePlaceholder')" multiple :browse-text="$t('core.chooseFiles')" @input="invoiceAdded"></b-form-file>
                    <small class="text-danger" v-if="errors['invoice']">{{ errors['invoice'][0] }}</small>
                    <div v-for="(file, fileIndex) in data.invoice">
                        {{ file.name }} <small @click="data.invoice.splice(fileIndex, 1)" class="user-select-none text-danger">{{ $t('core.actions.remove') }}</small>
                    </div>
                    <div v-for="(file, fileIndex) in data.invoices">
                        {{ file.filename }} <small @click="deleteExistingInvoice(fileIndex)" class="user-select-none text-danger">{{ $t('core.actions.remove') }}</small>
                    </div>
                </template>
            </Card>
<!--     INCOTERM       -->
            <Card class="col-12" :heading="$t('company.incoterm')" card-class="mt-5" v-if="canEditIncoterm">
                <div class="form-group">
                    <label class="">{{ $t('company.incoterm') }}</label>
                    <multiselect
                        :options="incoterms"
                        v-model="data.incoterm"
                        track-by="key"
                        :searchable="false"
                        :allow-empty="false"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                        label="key"
                    >
                        <template slot="option" slot-scope="{option}">
                            {{ option.key }} <small class="text-muted">{{ option.name }}</small>
                        </template>
                    </multiselect>
                    <small class="text-danger" v-if="errors['incoterm']">{{ errors['incoterm'][0] }}</small>
                </div>
            </Card>
<!--      FREIGHT PAYER      -->
            <Card class="col-12" card-class="mt-5 bg-muted text-muted">
                <h2>{{ $t('booking.freightPayer') }}</h2>
                <table class="table">
                    <tr class="text-muted">
                        <td>{{ $t('booking.fields.name') }}</td>
                        <td>{{ data.freightPayer.name }}</td>
                    </tr>
                    <tr class="text-muted">
                        <td>{{ $t('booking.fields.address') }}</td>
                        <td>{{ data.freightPayer.address }}</td>
                    </tr>
                    <tr class="text-muted">
                        <td>{{ $t('booking.fields.country') }}</td>
                        <td>{{ data.freightPayer.country }}</td>
                    </tr>
                    <tr class="text-muted">
                        <td>{{ $t('booking.fields.postal_code') }}</td>
                        <td>{{ data.freightPayer.postal_code }}</td>
                    </tr>
                    <tr class="text-muted">
                        <td>{{ $t('booking.fields.city') }}</td>
                        <td>{{ data.freightPayer.city }}</td>
                    </tr>
                </table>
            </Card>
<!--     DELIVERED BY SENDER       -->
            <Card class="col-12" v-if="canEditDeliveredBySender && !data.is_return" card-class="mt-5" :heading="$t('booking.fields.deliveredBySender')">
                <div class="form-group">
                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="delivered_by_sender" v-model="data.delivered_by_sender">
                        <label class="custom-control-label" for="delivered_by_sender">{{ $t('booking.fields.deliveredBySender') }}</label>
                    </div>
                </div>
            </Card>
<!--     SUBMIT BTN       -->
            <div class="col-12 my-3 text-right">
                <button class="btn btn-outline-primary" :disabled="isStoring" @click="data.is_draft = true; store()">{{ $t('booking.actions.saveDraft') }}</button>
                <button class="btn btn-primary" @click="data.is_draft = false; store()">{{ $t('booking.actions.store') }}</button>
            </div>
        </div>
    </div>
</template>

<script>

import { BDropdown, BDropdownItem, BFormFile, VBTooltip } from 'bootstrap-vue'
import Fuse from 'fuse.js'
import { objectToFormData } from 'object-to-formdata'
import { cloneDeep, pick } from 'lodash'
import AddressSearchInput from '@/Components/AddressSearchInput'
import NoDeliveryInCountryWarning from '@/Components/NoDeliveryInCountryWarning'
import GoodsCreaditLine from '@/Components/GoodsCreaditLine'
import Card from '@/Components/Card'
import LGTInput from '@/Components/LGTInput'
import CharacterCounter from '@/Components/CharacterCounter'

const fuseOptions = {
    shouldSort: true,
    threshold: 0.6,
    location: 0,
    distance: 50,
    maxPatternLength: 32,
    minMatchCharLength: 2,
    keys: [
        'name',
    ],
}

export default {
    name: 'BookingCreadit',
    components: { CharacterCounter, AddressSearchInput, BDropdown, BDropdownItem, BFormFile, NoDeliveryInCountryWarning, GoodsCreaditLine, Card, LGTInput },
    directives: { 'b-tooltip': VBTooltip },
    data: () => {
        return {
            loadingMetersRequired: lgt.loadingMetersRequired,
            volumeRequired: lgt.volumeRequired,
            imageDocumentationRequiredOnReturn: lgt.imageDocumentationRequiredOnReturn,
            senderLabelEmailSearchQuery: null,
            senderLabelEmailOptions: [],
            invoiceFieldData: null,
            imageDocumentationFieldData: null,
            isStoring: false,
            hasFreightPayer: lgt.hasFreightPayer,
            incoterms: lgt.incoterms,
            canEditIncoterm: lgt.canEditIncoterm,
            canEditDeliveredBySender: lgt.canEditDeliveredBySender,
            fuseRecipient: null,
            fuseLoadAddress: null,
            fuseUnloadAddress: null,
            emptyLine: {
                description: lgt.default_goods_description,
                packages: null,
                unit: null,
                weight: null,
                volume: null,
                loading_meters: null,
                value: null,
                value_currency: null,
            },
            nameFocused: false,
            unloadAddressNameFocused: false,
            loadAddressNameFocused: false,
            errors: {},
            selectedRecipient: null,
            selectedUnloadAddress: null,
            selectedLoadAddress: null,
            selectedSenderAddress: null,
            selectedDeliveryForm: null,
            recipients: [],
            unloadAddresses: [],
            loadAddresses: [],
            senderAddresses: [],
            countries: lgt.countries.filter((x) => x.offers_delivery === true),
            countriesAll: lgt.countries,
            loadAddressShow: false,
            unloadAddressShow: false,
            currencies: lgt.currencies,
            data: {
                id:null,
                incoterm: null,
                delivered_by_sender: lgt.deliveredBySenderDefault,
                additional_services: [],
                invoice: [],
                invoices: [],
                imageDocumentation: [],
                imageDocumentations: [],
                instructions: '',
                service_level: null,
                is_return: false,
                is_draft: false,
                sender: {
                    name: null,
                    address: null,
                    postal_code: null,
                    city: null,
                    country: null,
                    phone_prefix: null,
                    phone: null,
                    reference_number: '',
                    send_label: false,
                    label_email: (lgt.is_return) ? [] : null,
                    forFutureUse: false
                },
                recipient: {
                    name: '',
                    address: null,
                    address2: null,
                    postal_code: null,
                    city: null,
                    country: null,
                    reference_number: '',
                    forFutureUse: false,
                    phone: null,
                    email: null,
                    send_label: false,
                    label_email: null,
                    countryShort: null,
                    phone_prefix: null,
                },
                loadAddress: {
                    name: '',
                    address: null,
                    postal_code: null,
                    city: null,
                    country: null,
                    forFutureUse: false,
                },
                unloadAddress: {
                    name: '',
                    address: null,
                    address2: null,
                    postal_code: null,
                    city: null,
                    country: null,
                    forFutureUse: false,
                    phone: null,
                    email: null,
                    countryShort: null,
                },
                lines: [],
                freightPayer: lgt.freightPayer,
            },
        }
    },
    watch: {
        showInvoiceField: function(val){
          if(val === false){
              this.data.invoice = [];
          }
        },
        'data.sender.country': function (val) {
            if(!this.data.is_draft) {
                this.data.sender.phone_prefix = val.phone_prefix
            }
            if(this.data.is_return){
                let deliveryForm = this.countries
                    .find((x) => x.code === val.code)['services']
                    .find((x) => x.code === lgt.selectedDeliveryForm)
                if (typeof deliveryForm !== 'undefined') {
                    this.selectedDeliveryForm = deliveryForm
                    this.deliveryFormSelected(deliveryForm)
                } else {
                    this.selectedDeliveryForm = null
                }
            }


        },
        'data.recipient.country': function (val) {
            this.recipientOrUnloadCountryChanged('recipient', val);
        },
        'data.unloadAddress.country': function (val) {
            this.recipientOrUnloadCountryChanged('unloadAddress', val);
        },
        'data.sender' : {
            handler: function(){
                this.setFreightPayer();
            },
            deep: true
        },
        'data.recipient' : {
            handler: function(){
                this.setFreightPayer();
            },
            deep: true
        },
        'data.incoterm' : {
            handler: function(){
                this.setFreightPayer();
            },
            deep: true
        }
    },
    computed: {
        imageDocumentationErrors () {
            return Object.keys(this.errors).filter((key) => {
                return key.startsWith('imageDocumentation.')
            }).reduce((obj, key) => {
                const newKey = key.replace('imageDocumentation.', '')
                obj[newKey] = this.errors[key][0].replace('imageDocumentation.' + newKey, '')
                return obj
            }, {})

        },
        possibleServices () {
            if(this.data.is_return){
                if (this.data.sender.country === null  || this.data.sender.country === undefined) {
                    return []
                }
                return this.countries.find((x) => x.code === this.data.sender.country.code)['services'].find((x) => x.code === this.selectedDeliveryForm.code)?.services ?? []

            }

            if(this.unloadAddressShow === true){
                return (this.data.unloadAddress.country !== null && this.data.unloadAddress.country !== undefined)
                    ? this.countries.find((x) => x.code === this.data.unloadAddress.country.code)['services'].find((x) => x.code === this.selectedDeliveryForm.code)?.services ?? []
                    : []
            }

            if (this.data.recipient.country === null  || this.data.recipient.country === undefined) {
                return []
            }
            return this.countriesAll.find((x) => x.code === this.data.recipient.country.code)['services'].find((x) => x.code === this.selectedDeliveryForm.code)?.services ?? []
        },
        possibleDeliveryForms () {
            if(this.data.is_return){
                return (this.data.sender.country !== null && this.data.sender.country !== undefined) ? this.countries.find((x) => x.code === this.data.sender.country.code)['services'] : []
            }
            if(this.unloadAddressShow === true){
               return (this.data.unloadAddress.country !== null && this.data.unloadAddress.country !== undefined)
                    ? this.countries.find((x) => x.code === this.data.unloadAddress.country.code)['services']
                    : []
            }
            return (this.data.recipient.country !== null && this.data.recipient.country !== undefined) ? this.countriesAll.find((x) => x.code === this.data.recipient.country.code)['services'] : []
        },
        filteredRecipients () {
            return this.fuseRecipient !== null ? this.fuseRecipient.search(this.data.recipient.name).flatMap(x => x.item) : this.recipients;
        },
        filteredUnloadAddresses () {
            return this.fuseUnloadAddress !== null ? this.fuseUnloadAddress.search(this.data.unloadAddress.name).flatMap(x => x.item) : this.unloadAddresses;
        },
        filteredLoadAddresses () {
            return this.fuseLoadAddress !== null ? this.fuseLoadAddress.search(this.data.loadAddress.name).flatMap(x => x.item) : this.loadAddresses;
        },
        citySurcharge () {
            if (this.data.recipient.country === null || this.data.recipient.country === undefined) {
                return
            }
            return this.data.recipient.country.citySurcharges.filter((x) => x === this.data.recipient.postal_code).length > 0
        },
        showInvoiceField () {
            const countries = ['no', 'ch', 'li', 'gb', 'ie'];
            if(countries.includes(this.data.unloadAddress?.country?.code.toLowerCase())){
                return true;
            }
            if(countries.includes(this.data.recipient?.country?.code.toLowerCase())){
                return true;
            }
            if(this.data.is_return && countries.includes(this.data.sender?.country?.code.toLowerCase())){
                return true;
            }
            if(this.data.is_return && countries.includes(this.data.loadAddress?.country?.code.toLowerCase())){
                return true;
            }
            return false;
        },
        valueRequired() {
            const countries = ['no', 'ch', 'li', 'gb', 'ie'];
            if(lgt.valueRequired){
                return true;
            }
            if(this.data.additional_services.some(r=> ["B2B-15", "B2C-15", "B2C-115"].includes(r))){
                return true;
            }
            if(this.data.is_return && countries.includes(this.data.sender?.country?.code.toLowerCase())){
                return true;
            }
            return false;
        }
    },
    methods: {
        senderLabelEmailSearchQueryChange(value){
            this.senderLabelEmailSearchQuery = value;
        },
        senderLabelEmailClose(value, id){
            const exists = this.data.sender.label_email?.find((x) => x.email === this.senderLabelEmailSearchQuery);
            if(!exists && this.senderLabelEmailSearchQuery !== '' && this.senderLabelEmailSearchQuery !== null){
                this.addSenderEmail(this.senderLabelEmailSearchQuery);
            }
        },
        setSenderLabelEmail(emailString){
            if(this.data.is_return){
                const senderLabelEmails = emailString?.split(';').filter((x) => x !== '').map((email) => {
                    return { email: email }
                });
                if(senderLabelEmails === null || senderLabelEmails === undefined){
                    return;
                }
                this.senderLabelEmailOptions = [...senderLabelEmails];
                this.data.sender.label_email = [...senderLabelEmails];
            }
        },
        addSenderEmail(email){
            const tag = {
                email: email
            }
            if(this.data.sender.label_email === null || this.data.sender.label_email === ""){
                this.data.sender.label_email = [];
            }

            this.senderLabelEmailOptions.push(tag);
            this.data.sender.label_email.push(tag);
        },
        invoiceAdded(invoices){
            if(Array.isArray(invoices)){
                this.data.invoice = [...this.data.invoice, ...invoices];
            }else{
                this.data.invoice = [...this.data.invoice, invoices];
            }
            this.$refs['invoice-input'].reset()
        },
        imageDocumentationAdded(imageDocumentations){
            if(Array.isArray(imageDocumentations)){
                this.data.imageDocumentation = [...this.data.imageDocumentation, ...imageDocumentations];
            }else{
                this.data.imageDocumentation = [...this.data.imageDocumentation, imageDocumentations];
            }
            this.$refs['image-documentation-input'].reset()
        },
        setFreightPayer(){
            if(this.hasFreightPayer)return
            const val = this.data.incoterm?.key === 'EXW' ? this.data.recipient : this.data.sender;
            this.data.freightPayer.name = val.name;
            this.data.freightPayer.address = val.address;
            this.data.freightPayer.postal_code = val.postal_code;
            this.data.freightPayer.city = val.city;
            this.data.freightPayer.country = val.country?.code;
        },
        deleteAddress(field, address){
            const response = confirm(this.$t('booking.confirmAddressDelete'));
            if(response === false){
                return;
            }
            let index = this[field].findIndex((x) => x.id === address.id);
            let url = '/address/' + address.id;
            if(field === 'recipients'){
                url = '/recipient/' + address.id
            }
            axios.post(url, {"_method": "DELETE"}).then((response) => {
                this[field].splice(index,1);
                this.fuseRecipient = new Fuse(this.recipients, fuseOptions)
                this.fuseUnloadAddress = new Fuse(this.unloadAddresses, fuseOptions)
                this.fuseLoadAdress = new Fuse(this.loadAddresses, fuseOptions)
            }).catch((err) => {
            })
        },
        deleteExistingInvoice(index){
            axios.post('/bookings/' + this.data.id + '/invoice/' + this.data.invoices[index].id, {"_method": "DELETE"}).then((response) => {
                this.data.invoices.splice(index, 1);
            }).catch((err) => {
            })
        },
        deleteExistingImageDocumentation(index){
            axios.post('/bookings/' + this.data.id + '/image-documentation/' + this.data.imageDocumentations[index].id, {"_method": "DELETE"}).then((response) => {
                this.data.imageDocumentations.splice(index, 1);
            }).catch((err) => {
            })
        },
        recipientOrUnloadCountryChanged(field,val){
            if (val !== null && val !== undefined) {
                if(!this.data.is_return){
                    let deliveryForm = this.countries
                        .find((x) => x.code === val.code)?.services
                        .find((x) => x.code === lgt.selectedDeliveryForm)
                    if (typeof deliveryForm !== 'undefined') {
                        this.selectedDeliveryForm = deliveryForm
                        this.deliveryFormSelected(deliveryForm)
                    } else {
                        this.selectedDeliveryForm = null
                    }
                }
                this.data[field].countryShort = val.code
                if(!this.data.is_draft) {
                    this.data[field].phone_prefix = val.phone_prefix
                }
            }
        },
        simpleAddressChanged (field, val) {
            if(val.name !== ''){
                this.data[field].name = val.name
            }
            this.data[field].address = val.address
            this.data[field].address2 = val.address2
            this.data[field].postal_code = val.zipCode ?? val.postal_code
            this.data[field].city = val.city
            this.data[field].phone_prefix = val.phone_prefix
            this.data[field].phone = val.phone
            this.data[field].email = val.email
            this.data[field].country = this.countries.find((x) => x.code === val.countryShort)
        },
        fieldUnfocus (field) {
            setTimeout(() => {this[field] = false}, 200)
        },
        recipientSelected (val) {
            this.selectedRecipient = val
            if(this.data.is_return){
                this.data.sender.name = val.name
                this.data.sender.phone = val.phone
                this.data.sender.email = val.email
                //this.data.sender.send_label = val.send_label
                this.setSenderLabelEmail(val.label_email)
                //this.data.sender.label_email = val.label_email
                this.data.sender.postal_code = val.postal_code;
                val.countryShort = val.country;
                this.simpleAddressChanged('sender', val);
            }else{
                this.data.recipient.name = val.name
                this.data.recipient.phone = val.phone
                this.data.recipient.email = val.email
                this.data.recipient.send_label = val.send_label
                this.data.recipient.label_email =  val.label_email?.split(';').filter((x) => x !== '')[0];
                this.data.recipient.postal_code = val.postal_code;
                val.countryShort = val.country;
                this.simpleAddressChanged('recipient', val);
            }
        },
        senderSelected (val) {
            this.selectedSenderAddress = val
            if(this.data.is_return){
                this.data.recipient.name = val.name
                this.data.recipient.phone = val.phone
                this.data.recipient.email = val.email
                this.data.recipient.send_label = val.send_label
                this.data.recipient.label_email = val.label_email
                this.data.recipient.postal_code = val.postal_code;
                val.countryShort = val.country;
                this.simpleAddressChanged('recipient', val);
            }else{
                this.data.sender.name = val.name
                this.data.sender.phone = val.phone
                this.data.sender.email = val.email
                this.data.sender.send_label = val.send_label
                this.data.sender.label_email = val.label_email
                this.data.sender.postal_code = val.postal_code;
                val.countryShort = val.country;
                this.simpleAddressChanged('sender', val);
            }
        },
        unloadAddressSelected (val) {
            this.selectedUnloadAddress = val
            this.data.unloadAddress.name = val.name
            val.countryShort = val.country;
            this.simpleAddressChanged('unloadAddress', val);
        },
        loadAddressSelected (val) {
            this.selectedLoadAddress = val
            this.data.loadAddress.name = val.name
            val.countryShort = val.country;
            this.simpleAddressChanged('loadAddress', val);
        },
        deliveryFormSelected (val) {
            this.data.service_level = val.code
            this.data.additional_services = []
        },
        addPackage () {
            this.data.lines.push({ ...this.emptyLine })
        },
        store () {
            if(this.isStoring){
                return;
            }
            this.isStoring = true;
            let strippedData = cloneDeep(this.data)

            if(strippedData.sender.hasOwnProperty("addresses")){
                delete strippedData.sender.addresses;
            }

            if (strippedData.sender.country !== null && strippedData.sender.country !== undefined) {
                delete strippedData.sender.country.citySurcharges
                delete strippedData.sender.country.services
            }

            if (strippedData.recipient.recipient !== null) {
                delete strippedData.recipient.recipient
                delete strippedData.recipient.unload_address
                delete strippedData.recipient.load_address
            }

            if (strippedData.recipient.country !== null && strippedData.recipient.country !== undefined) {
                delete strippedData.recipient.country.citySurcharges
                delete strippedData.recipient.country.services
            }

            if (this.loadAddressShow && strippedData.loadAddress.country !== null && strippedData.loadAddress.country !== undefined) {
                delete strippedData.loadAddress.country.citySurcharges
                delete strippedData.loadAddress.country.services
            } else if (!this.loadAddressShow) {
                delete strippedData.loadAddress
            }

            if (this.unloadAddressShow && strippedData.unloadAddress.country !== null && strippedData.unloadAddress.country !== undefined) {
                delete strippedData.unloadAddress.country.citySurcharges
                delete strippedData.unloadAddress.country.services
            } else if (!this.unloadAddressShow) {
                delete strippedData.unloadAddress
            }

            const formData = objectToFormData(
                strippedData,
                {
                    indices: true,
                    booleansAsIntegers: true,
                },
            )
            axios.post('/bookings', formData).then((response) => {
                window.location.href = '/bookings?success'
            }).catch((err) => {
                this.isStoring = false;
                this.errors = err.response.data.errors
            })
        },
    },
    created () {
        this.emptyLine.unit = { code: 'KLL', name: this.$t('booking.unitTypes.KLL') }
        this.emptyLine.value_currency = lgt.currencies.length > 0 ? lgt.currencies[0].currency_code : null
        this.data.is_return = lgt.isReturn
        this.data.incoterm = lgt.incoterm

        if (this.data.is_return) {
            if (lgt.returnInfo !== undefined) {
                let info = lgt.returnInfo
                info.recipient = _.omit(info.recipient, 'reference_number')
                info = _.omit(info, 'reference_number')
                this.data.sender = { ...this.data.sender, ...info.recipient }
                this.data.recipient = { ...this.data.recipient, ...info }

                //if return booking, set the label emails as split out from the semicolon separated string
                this.setSenderLabelEmail(info.recipient.label_email);

                this.data.invoices = info.invoices;
                this.data.imageDocumentations = info.image_documentations;

                if(lgt.useAlternativeReturnAddress && !lgt.is_draft){
                    this.unloadAddressShow = true
                    this.data.unloadAddress = { ...this.data.unloadAddress, ...lgt.alternativeReturnAddress }
                } else if (info.loadAddress !== undefined) {
                    this.unloadAddressShow = true
                    this.data.unloadAddress = { ...this.data.unloadAddress, ...info.loadAddress }
                }



                if (info.unloadAddress !== undefined) {
                    this.loadAddressShow = true
                    this.data.loadAddress = { ...this.data.loadAddress, ...info.unloadAddress }
                }
            } else {
                this.data.recipient = { ...this.data.recipient, ...lgt.sender }
                this.data.recipient.country = this.countries.find((x) => x.code === this.data.recipient.country)
                if(lgt.useAlternativeReturnAddress){
                    this.unloadAddressShow = true
                    this.data.unloadAddress = { ...this.data.unloadAddress, ...lgt.alternativeReturnAddress }
                }
            }

            this.unloadAddresses = lgt.loadAddresses
            this.loadAddresses = lgt.unloadAddresses
            this.recipients = lgt.recipients
            this.senderAddresses = lgt.senderAddresses
            this.data.sender.send_label = true;
            this.addPackage()
        } else {
            this.data.sender = { ...this.data.sender, ...lgt.sender }

            if(Object.hasOwnProperty.call(lgt, 'booking')){
                const b = lgt.booking;
                this.data.id = b.id;
                this.data.is_draft = b.is_draft;
                this.data.is_return = b.is_return;
                const s = pick(b, 'name', 'address', 'postal_code', 'city','phone_prefix','phone', 'reference_number', 'send_label', 'label_email', 'country')
                s.country = this.countries.find((x) => x.code === s.country)
                this.data.sender = { ...this.data.sender, ...s };

                //if return booking, set the label emails as split out from the semicolon separated string
                this.setSenderLabelEmail(b.label_email);

                const r =  pick(b.recipient, 'name', 'address','address2', 'postal_code', 'city','phone_prefix','phone', 'reference_number', 'send_label', 'label_email', 'country', 'email')
                r.country = this.countries.find((x) => x.code === r.country)
                this.data.recipient = { ...this.data.recipient, ...r };
                this.data.invoices = b.invoices;
                this.data.imageDocumentations = b.image_documentations;

                this.data.lines = b.lines.map((i) => {
                    i.weight = i.weight !== 0 ? i.weight : null;
                    i.volume = i.volume !== 0 ? i.volume : null;
                    i.loading_meters = i.loading_meters !== 0 ? i.loading_meters : null;
                    i.value = i.value !== 0 ? i.value : null;
                    i.packages = i.packages !== 0 ? i.packages : null;

                    i.unit = { code: i.unit, name: this.$t('booking.unitTypes.' + i.unit) }
                    return i;
                });

                this.data.instructions = b.instruction;
                this.data.incoterm = lgt.allIncoterms.find((x) => x.key === b.incoterm);
                this.data.delivered_by_sender = b.delivered_by_sender;
                const loadA = b.load_address[0];
                if(loadA !== undefined) {
                    loadA.country = this.countries.find((x) => x.code === loadA.country)
                    this.data.loadAddress = {...this.data.loadAddress, ...loadA};
                    this.loadAddressShow = b.load_address.length > 0;
                }

                const unloadA = b.unload_address[0];
                if(unloadA !== undefined){
                    unloadA.country = this.countries.find((x) => x.code === unloadA.country)
                    this.data.unloadAddress = {...this.data.unloadAddress, ...unloadA};
                    this.unloadAddressShow = b.unload_address.length > 0;
                }

                this.$nextTick(() => {
                    //nextTick is needed as setting some of the previous data would otherwise overwrite
                    this.data.additional_services = b.additional_services;
                })

            }else{
                this.data.sender.country = this.countries.find((x) => x.code === this.data.sender.country)
                this.addPackage()
            }

            this.unloadAddresses = lgt.unloadAddresses
            this.loadAddresses = lgt.loadAddresses
            this.recipients = lgt.recipients
            this.senderAddresses = lgt.senderAddresses

            this.fuseRecipient = new Fuse(this.recipients, fuseOptions)
            this.fuseUnloadAddress = new Fuse(this.unloadAddresses, fuseOptions)
            this.fuseLoadAdress = new Fuse(this.loadAddresses, fuseOptions)
        }
    },
}
</script>
